import {
  HsMatrix4,
  HsQuaternion,
  HsVector3,
  hsGenerateConeGeometryData,
  hsGenerateCylinderGeometryData,
  hsIndexGeometry,
  hsMakeIndexedGeometry,
  hsMergeGeometries,
  type HsGeometry,
  type HsGraphics,
  type HsHypersceneTypes,
  type HsHypersceneViewer,
  type HsRegularGeometryData,
} from '@sibe/hyperscene';

/** A camera ray through a canvas point, in world space. */
export interface PointerRay {
  readonly origin: HsVector3;
  readonly direction: HsVector3;
}

export function makePointerRay(viewer: HsHypersceneViewer, clientX: number, clientY: number): PointerRay {
  const bounds: DOMRect = viewer.surface.canvas.getBoundingClientRect();
  const x: number = ((clientX - bounds.left) / bounds.width) * 2 - 1;
  const y: number = 1 - ((clientY - bounds.top) / bounds.height) * 2;
  const origin: HsVector3 = HsVector3.makeZeroVector().setFromMatrixPosition(viewer.camera.worldMatrix.values);
  const direction: HsVector3 = new HsVector3(x, y, 0.5)
    .unproject(viewer.camera.projectionMatrixInverse.values, viewer.camera.worldMatrix.values)
    .subtract(origin)
    .normalize();
  return { origin, direction };
}

/**
 * Parameter of the point on the line through `point` along world axis `axis` that is closest to the ray, or
 * null when the ray runs along the axis.
 */
export function closestAxisParameter(point: HsVector3, axis: number, ray: PointerRay): number | null {
  const offset: HsVector3 = HsVector3.makeCopy(point).subtract(ray.origin);
  const cosine: number = ray.direction.values[axis];
  const denominator: number = 1 - cosine * cosine;
  if (denominator < 1e-6) {
    return null;
  }
  return (cosine * HsVector3.dot(ray.direction, offset) - offset.values[axis]) / denominator;
}

/** An arrow along +Y from the origin to `length`: a thin shaft and a cone tip. */
export function makeArrowGeometry(graphics: HsGraphics, length: number): HsGeometry | null {
  const shaftLength: number = length * 0.8;
  const identity: HsHypersceneTypes.Array4 = HsQuaternion.makeIdentityQuaternion().values;
  const shaft: [HsRegularGeometryData, HsMatrix4] = [
    hsGenerateCylinderGeometryData(12),
    HsMatrix4.compose([0, shaftLength / 2, 0], identity, [0.08, shaftLength / 2, 0.08]),
  ];
  const tip: [HsRegularGeometryData, HsMatrix4] = [
    hsGenerateConeGeometryData(12),
    HsMatrix4.compose([0, shaftLength, 0], identity, [0.25, length - shaftLength, 0.25]),
  ];
  return hsMakeIndexedGeometry(graphics, hsIndexGeometry(hsMergeGeometries([shaft, tip])));
}

/** Rotation that turns +Y, the arrow direction, toward `sign` along world axis `axis`. */
export function makeDirectionRotation(axis: number, sign: number): HsQuaternion {
  if (axis === 0) {
    return HsQuaternion.makeFromAxisAndAngle([0, 0, 1], (-sign * Math.PI) / 2);
  }
  if (axis === 2) {
    return HsQuaternion.makeFromAxisAndAngle([1, 0, 0], (sign * Math.PI) / 2);
  }
  return sign > 0 ? HsQuaternion.makeIdentityQuaternion() : HsQuaternion.makeFromAxisAndAngle([1, 0, 0], Math.PI);
}

/**
 * Places the generated plane (XZ, ±1, normal +Y) as a rectangle with its normal along world axis `axis`, centered
 * at `center`, with half sizes `halfSizes` along the other two axes. The axes are permuted cyclically, so the
 * matrix keeps a positive determinant.
 */
export function makeFaceMatrix(
  axis: number,
  center: HsHypersceneTypes.Array3,
  halfSizes: HsHypersceneTypes.Array3
): HsMatrix4 {
  const first: number = (axis + 2) % 3;
  const second: number = (axis + 1) % 3;
  const values: number[] = new Array<number>(16).fill(0);
  values[first] = halfSizes[first];
  values[4 + axis] = 1;
  values[8 + second] = halfSizes[second];
  values[12] = center[0];
  values[13] = center[1];
  values[14] = center[2];
  values[15] = 1;
  return HsMatrix4.makeIdentityMatrix().setFromArray(values as HsHypersceneTypes.Array16);
}
